import { useState, useEffect } from 'react';
import { safeFetch } from '@/api/safeFetch';
import { Input, ButtonSiguiente, ButtonIconos } from '@/components/ui';
import { useNavigate } from 'react-router-dom';

export type ResultRow = {
  usuario_id: number;
  nombre_usuario: string;
  nombre: string;
  apellidos: string;
  email: string;
  dni: string | null;
  nie: string | null;
  profesional_id: number | null;
  nombre_empresa: string | null;
  cif: string | null;
  perfil_profesional_id: number | null;
  descripcion: string | null;
  foto_perfil: string | null;
  nombre_profesion?: string | null;
};

const typeOptions = ['auto', 'nombre', 'dni', 'nie', 'cif', 'empresa'] as const;

const BuscarProfesionales: React.FC = () => {
  const [q, setQ] = useState('');
  const [type, setType] = useState<typeof typeOptions[number]>('auto');
  const [results, setResults] = useState<ResultRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token') ?? '';

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setError(null);
      return;
    }
    const timer = setTimeout(() => doSearch(), 300);
    return () => clearTimeout(timer);
  }, [q, type]);

  const doSearch = async () => {
    const controller = new AbortController();
    const signal = controller.signal;

    setLoading(true);
    setError(null);

    try {
      const url = `/api/profesionales/buscar?q=${encodeURIComponent(q.trim())}&type=${encodeURIComponent(type)}`;
      const res = await safeFetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        signal
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message || 'Error buscando profesionales');
        setResults([]);
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  };


  useEffect(() => {
    return () => {
      setQ("");
      setType("auto");
      setResults([]);
      setLoading(false);
      setError(null);
    };
  }, []);


  return (
    <div className="w-full h-screen p-4 flex items-center justify-center bg-zinc-500 relative py-8">
        
         {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

      <div className="w-full max-w-3xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 flex flex-col gap-6 shadow-blue-700 h-[calc(90vh-4rem)]">
        <h2 className="text-2xl font-bold mb-4 text-center">Buscar Profesionales</h2>

        {/* Filtro y búsqueda */}
        <div className="w-full bg-blue-600 p-4 rounded-xl flex gap-4 items-center border-zinc-200 border-r-4 border-t-2 border shadow-lg shadow-blue-700">
          <div className="flex-1">
            <label className="font-medium block mb-1 text-white">Tipo</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as typeof typeOptions[number])}
              className="w-full border-2 border-zinc-600 hover:border-blue-700 p-2 rounded-md h-[36px] dark:bg-white dark:text-zinc-600"
            >
              {typeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === 'dni' ? 'DNI/NIE' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="font-medium block mb-0.5 text-white">Buscar</label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, dni, nie, cif o empresa..."
              className="w-full border p-2 rounded h-[36px]"
            />
          </div>

          <div className="flex-1">
            <label className="opacity-0 block mb-1">.</label>
            <ButtonSiguiente onClick={doSearch} className="w-full h-[32px] mb-1">
              Buscar
            </ButtonSiguiente>
          </div>
        </div>

        {/* Mensajes de estado */}
        {loading && <div>Buscando...</div>}
        {error && <div className="text-red-600">{error}</div>}

        {/* Resultados */}
        <div className="flex-1 overflow-y-auto space-y-3">
          {results.length === 0 && !loading && q.trim() && (
            <div className="text-zinc-500">No se encontraron resultados.</div>
          )}

          {results.map((r) => (
            <div
              key={`${r.perfil_profesional_id ?? r.usuario_id}`}
              className="p-3 bg-white rounded shadow flex gap-4 items-center cursor-pointer hover:bg-gray-100"
            >
              <img
                src={r.foto_perfil ? `${import.meta.env.VITE_BACKEND_URL}/${r.foto_perfil.replace(/^\/+/, "")}` : "/img/logo/LogoLinkServs.png"}
                alt={r.nombre_empresa || `${r.nombre} ${r.apellidos}`}
                className="w-16 h-16 object-cover rounded"
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/img/logo/LogoLinkServs.png"; }}
              />
              <div className="flex-1">
                <div className="font-semibold">{r.nombre} {r.apellidos} {r.nombre_empresa ? `— ${r.nombre_empresa}` : ""}</div>
                <div className="text-sm text-zinc-600">{r.email} · {r.dni ?? r.nie ?? ""}</div>
                <div className="text-sm text-zinc-500">{r.descripcion}</div>
              </div>

              {/* Botón Ver: navega a ProfesionalBlock */}
              {r.perfil_profesional_id && (
                <ButtonIconos
                  onClick={() =>
                    window.open(
                      `/profesional/${r.nombre_profesion || "general"}/${r.perfil_profesional_id}`,
                      "_blank"
                    )
                  }
                  className="px-3 py-1 bg-blue-600 text-white rounded"
                >
                  Ver
                </ButtonIconos>
              )}

            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BuscarProfesionales;
